/**
 * Computer requirements for the desktop app, shown on
 * /technology/scanner-plotter-compatibility#computer and summarised in the FAQ.
 * Facts that come from the app build (keep in step with ~/Downloads/ppfengine
 * electron/electron-builder.cjs + UPDATES_AND_DISTRIBUTION_BRIEF.md): mac is
 * arm64 only, Windows is x64 only, Electron 43 needs macOS 12+, the installed
 * app is ~730 MB. The memory/CPU/GPU tiers are targets until they are confirmed
 * on clean machines before delivery; change them here and only here.
 */
export type Platform = 'mac' | 'windows';

export interface SpecRow {
	label: string;
	min: string;
	rec: string;
	/** Optional small print under the pair. */
	note?: string;
}

export interface PlatformSpec {
	id: Platform;
	name: string;
	/** Mono subtitle on the toggle. */
	arch: string;
	/** One line on how far we have run it ourselves. Never overstate. */
	status: string;
	tested: boolean;
	rows: SpecRow[];
}

const shared = {
	storage: {
		label: 'Storage',
		min: '4 GB free',
		rec: '25 GB free on an SSD',
		note: 'Projects keep the original scan, and a dense scan can be 1 GB on its own.'
	},
	display: {
		label: 'Display',
		min: '1440 × 900',
		rec: '1680 × 1050 or larger, a 16-inch laptop or an external monitor',
		note: 'The 3D view and the 2D editor sit side by side. More room, less panning.'
	}
} satisfies Record<string, SpecRow>;

export const platforms: PlatformSpec[] = [
	{
		id: 'mac',
		name: 'macOS',
		arch: 'Apple Silicon',
		status: 'Runs in our studio every day',
		tested: true,
		rows: [
			{ label: 'Processor', min: 'Apple M1', rec: 'M2 Pro, M3 or newer' },
			{ label: 'System', min: 'macOS 12 Monterey', rec: 'macOS 14 Sonoma or later' },
			{
				label: 'Memory',
				min: '8 GB',
				rec: '16 GB or more',
				note: 'The one to spend on. Memory decides how dense a scan you can work on comfortably.'
			},
			{ label: 'Graphics', min: 'Built into the chip', rec: 'Built into the chip' },
			shared.storage,
			shared.display
		]
	},
	{
		id: 'windows',
		name: 'Windows',
		arch: '64-bit x64',
		status: 'Checked on a clean PC before delivery',
		tested: false,
		rows: [
			{
				label: 'Processor',
				min: '4 cores, Intel Core i5 8th gen or Ryzen 5 2600',
				rec: '8 cores, Intel Core i7 12th gen or Ryzen 7 5800X'
			},
			{ label: 'System', min: 'Windows 10 64-bit', rec: 'Windows 11' },
			{
				label: 'Memory',
				min: '8 GB',
				rec: '16 GB or more',
				note: 'The one to spend on. Memory decides how dense a scan you can work on comfortably.'
			},
			{
				label: 'Graphics',
				min: 'DirectX 11 with WebGL 2, Intel UHD 620 class',
				rec: 'Dedicated 4 GB GPU, GeForce GTX 1650 or Radeon RX 6500 class'
			},
			shared.storage,
			shared.display
		]
	}
];

/** Hardware it will not install on, stated plainly. */
export const unsupported = ['Intel Macs', 'Linux', 'iPad and tablets', 'Chromebooks'] as const;

/** The machine the app is built and run on, for the "tested" claim. */
export const studioMachine = 'MacBook Pro · Apple M3 Max · 48 GB';

/** FAQ summary: derived wording, kept next to the table it summarises. */
export const requirementsSummary =
	'An Apple Silicon Mac (M1 or newer, macOS 12 or later) or a 64-bit Windows 10 or 11 PC. 8 GB of memory is the minimum and 16 GB is recommended, with a few GB free for projects. The full list, and a check for the computer you are on, is on the compatibility page.';
